import {
    Component,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    Input,
    inject,
    ElementRef,
    AfterViewInit, ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { TransitionService, Transition } from '@uirouter/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { COOKIE_CATEGORIES } from '@monsido/core/constants/cookie-categories.constant';
import { COOKIE_SOURCE } from '@monsido/core/constants/cookie-source.constant';
import { COOKIE_FIELDS } from '@monsido/core/constants/cookie-fields.constant';
import { COOKIE_BANNER_I18N } from '@monsido/modules/cookie-banner/constants/cookie-banner-i18n.constant';
import { LOCALE_LANGUAGE_MAP } from '@client/app/modules/languages-ng2/constants/locale-language-map.constant';
import { TRAFFIC_LIGHT_COLORS } from '@client/app/modules/cookie-banner/constants/cookie-banner-traffic-light';
import { CookieOverviewService } from './cookie-overview.service';
import { CookieCategoriesLocalizeService } from './categories-localize/cookie-categories-localize.service';
import { CookieFieldsLocalizeService } from './cookie-fields-localize/cookie-fields-localize.service';
import { CookieModelService } from './cookie-model.service';
import { DomainService } from '@client/app/services/entities/domain/domain.service';
import { CoreDomainService } from '@client/app/core/services/domain/core-domain.service';
import { SessionService } from '@monsido/core/session/session.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { DemoHasAccessService } from '@client/app/modules/demodata/services/demo-has-access.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { deepEqual, pick, throttle, safeClone } from '@client/app/shared/utils/object-utils';
import { FormUtils } from '@client/ng2/shared/form-utils';

@Component({
    selector: 'cookie-banner-cookie-overview',
    standalone: false,
    template: `
        <form #cookieOverviewForm="ngForm"
              class="form-horizontal cookie-banner-cookie-overview-dialog"
              (ngSubmit)="submit()">
            <div class="row justify-content-end">
                <div class="d-flex mr-1 align-items-center mt-1 mb-2">
                    <filter-button [showFiltersBtn]="showFilters"
                                   [(isFiltersOpen)]="isFiltersOpen">
                    </filter-button>
                    <table-search [minSearchLength]="3"
                                  class="block outer-r-small"
                                  placeholderTxt="{{'Search...' | translate}}"
                                  [(model)]="search"
                                  (getPage)="onSearch($event)">
                    </table-search>
                </div>
            </div>
            <div class="bounds_for_absolute">
                <div class="card panel-monsido-card">
                    <div class="card-header border-bottom-none">
                        <div class="d-flex">
                            <div class="col-48 mt-2 mb-1 pl-1">
                                <h2 class="inline-block"
                                    translate>
                                    Include cookies
                                </h2>
                                <span class="fas fa-question-circle fa-question-circle-absolute inline-block ml-1"
                                      aria-hidden="true"
                                      [ngbTooltip]="'Give an overview of the cookies used on your website. Finish a domain scan or add them manually.' | translate"
                                      placement="top"
                                      [container]="'body'">
                                </span>
                                <div *ngIf="!hasCrawlStatus && !isDemoAccount && !hasOnDemandScans"
                                     class="inline">
                                    <span class="btn btn-border-radius btn-secondary pull-right mon-disabled"
                                          data-test="scan-button"
                                          role="button"
                                          [triggers]="'focus hover'"
                                          placement="left"
                                          [ngbTooltip]="'Your current plan does not include on-demand scans. Please reach out to your Acquia Web Governance representative to learn more about this option' | translate"
                                          tabindex="0"
                                          aria-disabled="true">
                                        <span class="fa fas fa-sync mr-1"
                                              aria-hidden="true"></span>
                                        <span translate>Scan all pages for cookies</span>
                                    </span>
                                </div>
                                <div *ngIf="!hasCrawlStatus && !isDemoAccount && hasOnDemandScans"
                                     class="inline">
                                    <span class="btn btn-border-radius btn-secondary pull-right"
                                          data-test="scan-button"
                                          role="button"
                                          monEventTrigger="actionTrigger"
                                          [monEventParams]="{action:'btn-new-cookie-scan'}"
                                          [triggers]="'focus hover'"
                                          placement="left"
                                          [ngbTooltip]="'The scan can take a few hours/days to finalise, depending on the size of the website' | translate"
                                          [monModalConfirm]="'The scan can take a few hours/days to finalise, depending on the size of the website.<br><br>Are you sure you want to start on-demand scan?' | translate"
                                          (monModalConfirmAction)="onDemandScan()"
                                          tabindex="0">
                                        <span class="fa fas fa-sync mr-1"
                                              aria-hidden="true"></span>
                                        <span translate>Scan all pages for cookies</span>
                                    </span>
                                </div>
                                <span class="btn btn-border-radius btn-secondary pull-right"
                                      *ngIf="isDemoAccount"
                                      role="button"
                                      [triggers]="'focus hover'"
                                      placement="left"
                                      [ngbTooltip]="' This scan feature is not available on a demo account' | translate"
                                      (click)="demoAccountScanWarning()"
                                      tabindex="0">
                                    <span class="fa fas fa-sync mr-1"
                                          aria-hidden="true"></span>
                                    <span translate>Scan all pages for cookies</span>
                                </span>
                                <button *ngIf="hasCrawlStatus"
                                        type="button"
                                        class="text-nowrap btn btn-border-radius btn-secondary pull-right"
                                        [triggers]="'focus hover'"
                                        placement="left"
                                        [ngbTooltip]="'Crawling' | translate"
                                        (click)="openScanDetails()">
                                    <span class="text-dark-grey fas fa-spin fa-spinner outer-r-smurf"></span>
                                    <span class="text-grey"
                                          translate>Scanning in progress</span>
                                    <span class="text-dark-grey">
                                        ({{ getDomainCrawlStatus() }} %)
                                    </span>
                                    <span class="text-link ml-2"
                                          translate>
                                        Crawl info
                                    </span>
                                </button>
                                <div class="pull-right pr-2 pt-1"
                                     *ngIf="showReminder">
                                    <span class="text-link fas fa-info-circle"
                                          aria-hidden="true"></span>
                                    <span translate>Don't forget to update your languages in Script Setup!</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <mon-cookie-overview-language-tabs
                                [monShowLanguages]="filledLanguages"
                                [monLanguages]="languageOptions"
                                [(monModel)]="selectedLanguage"
                                (monOnAddLanguage)="addLanguage($event)"
                                (monOnRemoveLanguage)="removeLanguage($event)"
                                (monModelChange)="onLanguageChange()">
                        </mon-cookie-overview-language-tabs>
                    </div>
                    <div *ngIf="isFiltersOpen"
                         class="filters-wrapper card-header pl-2">
                        <table-filters [(open)]="isFiltersOpen"
                                       [(ngModel)]="filters"
                                       name="tableFilters"
                                       [filters]="availableFilters"
                                       (onChanges)="onFiltersChange($event)">
                        </table-filters>
                    </div>
                    <div class="card-body pb-4 pt-4 px-0">
                        <div *ngIf="!sortedInCookies.length">
                            <p class="pl-9"
                               translate>Start a new scan to find cookies or add them manually</p>
                            <span aria-hidden="true"
                                  class="pl-9"
                                  [ngbTooltip]="'Add row' | translate"
                                  placement="right">
                                <button type="button"
                                        class="btn btn-border-radius btn-secondary"
                                        (click)="addNewCookieEntry()">
                                    <span class="fas fa-plus fa-fw"
                                          aria-hidden="true"></span>
                                    <span class="sr-only"
                                          translate>Add cookie</span>
                                </button>
                            </span>
                        </div>
                        <table-container *ngIf="sortedInCookies.length"
                                         mon-tab-control-parent
                                         noResultMessage="'No cookies found' | translate"
                                         [collection]="sortedInCookies"
                                         (onPerPageChange)="onPageSizeChange($event)"
                                         (onPageChange)="onPageChange($event)">
                            <div class="flex_table">
                                <div class="left_section">
                                    <div></div>
                                    <div *ngFor="let cookie of sortedInCookies">
                                        <div class="left_section_icon_wrapper">
                                    <span *ngIf="cookie.source === 'manual'"
                                          role="img"
                                          [attr.aria-label]="'Cookie added by user' | translate"
                                          class="text-secondary-5"
                                          [ngClass]="['SIDEMENUES', 'DATA_PRIVACY', 'CONSENT_OVERVIEW', 'USER_ADDED_COOKIE'] | monIcons"></span>
                                        </div>
                                    </div>
                                </div>
                                <div class="middle_section">
                                    <div class="middle_section_content">
                                        <div>
                                            <div><span translate>Category</span></div>
                                            <div><span translate>Name</span></div>
                                            <div><span translate>Domain</span></div>
                                            <div>
                                                <span translate>Platform</span>
                                                <span class="fas fa-question-circle py-1 inline-block ml-1"
                                                      aria-hidden="true"
                                                      [ngbTooltip]="'Add the name of the platform or company who provides this cookie.' | translate"
                                                      placement="top"
                                                      [container]="'body'">
                                        </span>
                                            </div>
                                            <div>
                                                <span translate>Description</span>
                                                <span class="fas fa-question-circle py-1 inline-block ml-1"
                                                      aria-hidden="true"
                                                      [ngbTooltip]="'Give a short description of the purpose of the cookie' | translate"
                                                      placement="top"
                                                      [container]="'body'">
                                        </span>
                                            </div>
                                            <div>
                                                <span translate>Duration</span>
                                                <span class="fas fa-question-circle py-1 inline-block ml-1"
                                                      aria-hidden="true"
                                                      [ngbTooltip]="'How long is the cookie active' | translate"
                                                      placement="top"
                                                      [container]="'body'">
                                        </span>
                                            </div>
                                        </div>
                                        <div *ngFor="let cookie of sortedInCookies; let $index = index;">
                                            <div class="position-relative-in-ie-only">
                                                <form-field-select returnValue="value"
                                                                   (ngChange)="cookieEntryTouched(cookie, $index)"
                                                                   [name]="'cookieCategories' + $index"
                                                                   [(ngModel)]="cookie.type"
                                                                   [monTabControl]="$index"
                                                                   [options]="cookieCategories">
                                                </form-field-select>
                                            </div>
                                            <div>
                                                <input class="form-control"
                                                       [(ngModel)]="cookie.name"
                                                       [name]="'cookieName' + $index"
                                                       data-test="cookie-source-input"
                                                       (change)="cookieEntryTouched(cookie, $index)"
                                                       [readonly]="cookie.source !== cookieSourceConstant.manual"
                                                       required="true"
                                                       placeholder="{{ 'Cookie name' | translate }}"
                                                       [monTabControl]="$index"
                                                       type="text"/>
                                            </div>
                                            <div>
                                                <input [(ngModel)]="cookie.domain"
                                                       [name]="'cookieDomain' + $index"
                                                       data-test="cookie-domain-input"
                                                       (change)="cookieEntryTouched(cookie, $index)"
                                                       [readonly]="cookie.source !== cookieSourceConstant.manual"
                                                       placeholder="{{ 'Cookie domain' | translate }}"
                                                       [monTabControl]="$index"
                                                       type="text"
                                                       class="form-control"/>
                                            </div>
                                            <div>
                                                <input class="form-control"
                                                       [(ngModel)]="cookie.platform"
                                                       [name]="'cookiePlatform' + $index"
                                                       data-test="cookie-platform-input"
                                                       (change)="cookieEntryTouched(cookie, $index)"
                                                       placeholder="{{ 'Cookie platform' | translate }}"
                                                       [monTabControl]="$index"
                                                       type="text"/>
                                            </div>
                                            <div>
                                                <input [(ngModel)]="sortedInCookiesWithLanguageMap[$index].descriptionMap[selectedLanguage]"
                                                       [name]="'cookieDescription' + $index"
                                                       data-test="cookie-domain-input"
                                                       (change)="cookieEntryTouched(cookie, $index)"
                                                       placeholder="{{ 'Cookie description' | translate }}"
                                                       class="form-control"
                                                       [monTabControl]="$index"
                                                       type="text"/>
                                            </div>
                                            <div>
                                                <input [(ngModel)]="sortedInCookiesWithLanguageMap[$index].expirationMap[selectedLanguage]"
                                                       [name]="'cookieExpiresInput' + $index"
                                                       data-test="cookie-expires-input"
                                                       (change)="cookieEntryTouched(cookie, $index)"
                                                       placeholder="{{ 'Cookie duration' | translate }}"
                                                       [monTabControl]="$index"
                                                       type="text"
                                                       class="form-control"/>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="right_section">
                                    <div></div>
                                    <div *ngFor="let cookie of sortedInCookies; let $index = index;">
                                    <span aria-hidden="true"
                                          class="mr-2"
                                          [ngbTooltip]="'Remove row' | translate"
                                          placement="top">
                                        <button type="button"
                                                data-test="delete-cookie-button"
                                                [monTabControl]="$index"
                                                class="btn btn-border-radius btn-secondary"
                                                (click)="removeCookieEntry(cookie.id, $index)"
                                                [ngbTooltip]="'Remove row' | translate"
                                                placement="top">
                                            <span class="fas fa-trash-alt fa-fw"
                                                  aria-hidden="true"></span>
                                            <span class="sr-only"
                                                  translate>Remove cookie</span>
                                        </button>
                                    </span>
                                    <cookie-banner-traffic-light
                                            *ngIf="trafficLights[$index].color !== trafficLightConstant.yellow"
                                            placement="left"
                                            [ngbTooltip]="trafficLights[$index].tooltip"
                                            role="img"
                                            [attr.aria-label]="trafficLights[$index].tooltip"
                                            [type]="trafficLights[$index].color" />
                                    <cookie-banner-traffic-light
                                            *ngIf="trafficLights[$index].color === trafficLightConstant.yellow"
                                            [attr.aria-label]="trafficLights[$index].tooltip"
                                            placement="left"
                                            [ngbTooltip]="trafficLights[$index].tooltip"
                                            [type]="trafficLights[$index].color"
                                            [monTabControl]="$index"
                                            role="button"
                                            (click)="resolveAbsenceWarning(cookie, $index)" />
                                    </div>
                                </div>
                            </div>
                            <span aria-hidden="true"
                                  class="pl-9"
                                  [ngbTooltip]="'Add row' | translate"
                                  placement="right">
                                <button type="button"
                                        data-test="add-cookie-button"
                                        class="btn btn-border-radius btn-secondary"
                                        (click)="addNewCookieEntry()">
                                    <span class="fas fa-plus fa-fw" aria-hidden="true"></span>
                                    <span class="sr-only" translate>Add cookie</span>
                                </button>
                            </span>
                        </table-container>
                    </div>
                    <div class="card-footer d-flex justify-content-between align-items-center">
                        <div>
                            <p class="mb-0" translate>Save this overview to publish completed cookies to the Cookie Overview.</p>
                            <p class="m-0" translate>You need to include a name in order to save your cookies.</p>
                        </div>
                        <div>
                            <button type="submit"
                                    data-test="save-cookies-button"
                                    class="btn btn-border-radius btn-save"
                                    [disabled]="saving"
                                    monEventTrigger="actionTrigger"
                                    [monEventParams]="{action: 'create-cookie-overview-save'}">
                                <span [ngClass]="{'fas fa-spinner fa-spin fa-fw ': saving}" aria-hidden="true"></span>
                                <span translate>Save</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    `,
})
export class CookieBannerCookieOverviewComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
    @Input() domain: any;
    @Input() cookies: any;

    @ViewChild('cookieOverviewForm') cookieOverviewForm?: NgForm;

    search: string = '';
    sortedInCookies: any[] = [];
    sortedInCookiesWithLanguageMap: any[] = [];
    perPage: number = 10;
    currentPageNumber: number = 1;
    showFilters: boolean = true;
    isFiltersOpen: boolean = false;
    restoredCookies: any[] = [];
    showReminder: boolean = false;
    languageOptions: any[] = [];
    filledLanguages: any[] = [];
    selectedLanguage: string = '';
    cookiesInstance: any[] = [];
    cookiesInstanceLanguageMap: any[] = [];
    originCookies: any[] = [];
    toBeUpdated: any[] = [];
    toBeRemoved: any[] = [];
    saving: boolean = false;
    isDemoAccount: boolean = false;
    hasOnDemandScans: boolean = false;
    hasCrawlStatus: boolean = false;
    cookieCategories: any[] = [];
    availableFilters: any = {};
    filters: any = {};
    trafficLights: any[] = [];
    cookieSourceConstant = COOKIE_SOURCE;
    trafficLightConstant = TRAFFIC_LIGHT_COLORS;
    totalPages = 1;

    private transListenerOff: any;
    private cookieValidationProps = ['name', 'domain', 'path'];
    private availableLanguages = Object.values(COOKIE_BANNER_I18N);
    private elementRef = inject(ElementRef);

    constructor (
        private cookieOverviewService: CookieOverviewService,
        private cookieCategoriesLocalizeService: CookieCategoriesLocalizeService,
        private cookieFieldsLocalizeService: CookieFieldsLocalizeService,
        private cookieModelService: CookieModelService,
        private domainService: DomainService,
        private coreDomainService: CoreDomainService,
        private sessionService: SessionService,
        private monModalService: MonModalService,
        private demoHasAccessService: DemoHasAccessService,
        private activeFeatureService: ActiveFeatureService,
        private translateService: TranslateService,
        private transitions: TransitionService,
    ) {}

    ngOnInit (): void {
        this.initializeComponent();
    }

    ngOnChanges (changes: SimpleChanges): void {
        if (changes['cookies']) {
            const filteredCookies = this.filterDuplicates(this.cookies);
            this.cookiesInstance = safeClone(filteredCookies);
            this.cookiesInstanceLanguageMap = this.getCookiesInstanceLanguageMap();
            this.originCookies = safeClone(this.cookiesInstance);
            this.toBeUpdated = [];
            this.toBeRemoved = [];
            this.updateFilteredCookies();

            this.filledLanguages = this.getFilledLanguages(this.cookies);

            if (this.filledLanguages.length === 0 && this.languageOptions.length > 0) {
                this.filledLanguages.push(this.languageOptions[0]);
                this.selectedLanguage = this.filledLanguages[0].value;
            }

            if (this.filledLanguages.length > 0) {
                this.selectedLanguage = this.selectedLanguage || this.filledLanguages[0].value;
            }
        }
    }

    ngOnDestroy (): void {
        if (typeof this.transListenerOff === 'function') {
            this.transListenerOff();
        }
        window.removeEventListener('resize', this.debouncedAdjustDropdownsWidth);
    }

    trackByCookieIndex (index: number, item: any): any {
        return index;
    }

    private initializeComponent (): void {
        this.showReminder = false;
        this.languageOptions = this.availableLanguages.map((key: string) => {
            const languageLabel = LOCALE_LANGUAGE_MAP[key as keyof typeof LOCALE_LANGUAGE_MAP];
            return {
                name: languageLabel ? languageLabel : key,
                value: key,
            };
        });

        this.isDemoAccount = this.demoHasAccessService.isDemoConsentManager();
        this.cookieCategories = [
            {
                name: this.cookieCategoriesLocalizeService[COOKIE_CATEGORIES.essential],
                value: COOKIE_CATEGORIES.essential,
            },
            {
                name: this.cookieCategoriesLocalizeService[COOKIE_CATEGORIES.basic],
                value: COOKIE_CATEGORIES.basic,
            },
            {
                name: this.cookieCategoriesLocalizeService[COOKIE_CATEGORIES.marketing],
                value: COOKIE_CATEGORIES.marketing,
            },
            {
                name: this.cookieCategoriesLocalizeService[COOKIE_CATEGORIES.analytics],
                value: COOKIE_CATEGORIES.analytics,
            },
            {
                name: this.cookieCategoriesLocalizeService[COOKIE_CATEGORIES.personalisation],
                value: COOKIE_CATEGORIES.personalisation,
            },
        ];

        this.cookiesInstance = this.filterDuplicates(this.cookies);
        this.cookiesInstanceLanguageMap = this.getCookiesInstanceLanguageMap();
        this.originCookies = safeClone(this.cookiesInstance);
        this.hasOnDemandScans = this.activeFeatureService.isFeatureActivePure('on_demand_scans');
        this.hasCrawlStatus = this.coreDomainService.hasCrawlStatus();

        this.initFilters();
        this.initTransitionListener();

        window.addEventListener('resize', this.debouncedAdjustDropdownsWidth);
    }

    ngAfterViewInit (): void {
        const scrollableSection = this.elementRef.nativeElement.querySelector('.middle_section');
        if (scrollableSection) {
            scrollableSection.addEventListener(
                'scroll',
                throttle(function () {
                    const shift = -scrollableSection.scrollLeft + 'px';
                    Array.prototype.forEach.call(scrollableSection.querySelectorAll('form-field-select'), function (el) {
                        el.style.setProperty('margin-left', shift);
                    });
                }, 50),
            );
            this.hackForFixingDropdownBehavior(scrollableSection);
        }
    }

    private initFilters (): void {
        this.availableFilters = {
            category: this.cookieCategories,
        };
        this.filters = {
            category: [],
        };
    }

    private initTransitionListener (): void {
        this.transListenerOff = this.transitions.onBefore({}, (transition: Transition) => {
            if (!this.toBeUpdated.length && !this.toBeRemoved.length && this.originCookies.length === this.cookiesInstance.length) {
                return true;
            }

            const params = {
                transition: transition,
                callback: () => this.submit(),
                options: {
                    form: { $valid: true }, // Simplified form validation
                    listenerRef: this.transListenerOff,
                },
            };

            if (!this.isDemoAccount) {
                this.cookieOverviewService.confirmSavePopup(params);
                return false;
            }
            return true;
        });
    }

    onFiltersChange (changes: any): void {
        if (!changes.category.length) {
            this.initFilters();
        }

        if (changes.category) {
            this.updateFilteredCookies();
        }
    }

    onFilterChange (event: any, category: any): void {
        // Simple filter handling for the checkbox filters
        const isChecked = event.target.checked;
        if (!this.filters.categories) {
            this.filters.categories = [];
        }

        if (isChecked) {
            if (!this.filters.categories.includes(category.value)) {
                this.filters.categories.push(category.value);
            }
        } else {
            this.filters.categories = this.filters.categories.filter((cat: any) => cat !== category.value);
        }

        this.onFiltersChange(this.filters);
    }

    onPageSizeChange (perPage: any): void {
        this.perPage = Number(perPage);
        this.updateFilteredCookies();
    }

    onPageChange (page: number): void {
        this.currentPageNumber = Number(page);
        this.updateFilteredCookies();
    }

    onSearch (search: string): void {
        this.search = search;
        this.updateFilteredCookies();
    }

    addNewCookieEntry (): void {
        const cookieOptions: any = {};
        cookieOptions[COOKIE_FIELDS.name] = '';
        cookieOptions[COOKIE_FIELDS.domain] = '';
        cookieOptions[COOKIE_FIELDS.platform] = '';
        cookieOptions[COOKIE_FIELDS.source] = COOKIE_SOURCE.manual;
        cookieOptions[COOKIE_FIELDS.type] = this.cookieCategories[0].value;

        cookieOptions[COOKIE_FIELDS.expirations] = [];
        cookieOptions[COOKIE_FIELDS.descriptions] = [];
        this.filledLanguages.forEach((languageOption: any) => {
            cookieOptions[COOKIE_FIELDS.expirations].push({ language: languageOption.value, text: '' });
            cookieOptions[COOKIE_FIELDS.descriptions].push({ language: languageOption.value, text: '' });
        });

        this.cookiesInstance.push(this.cookieModelService.createCookie(cookieOptions));

        const total = this.cookiesInstance.length;

        if (total >= this.perPage) {
            this.currentPageNumber = total % this.perPage !== 0 ? Math.floor(total / this.perPage + 1) : total / this.perPage;
        }

        this.updateFilteredCookies();
    }

    removeCookieEntry (id: any, cookieIndex: number): void {
        let index: number;
        if (id === undefined && cookieIndex !== undefined) {
            index = this.perPage * (this.currentPageNumber - 1) + cookieIndex;
        } else {
            index = this.cookiesInstance.findIndex((cookie: any) => cookie.id === id);
        }

        if (this.cookiesInstance[index].id != null) {
            this.toBeRemoved.push(safeClone(this.cookiesInstance[index]));
        }

        this.toBeUpdated = this.toBeUpdated.filter((cookie: any) => id !== cookie.id);

        this.cookiesInstance.splice(index, 1);
        this.cookiesInstanceLanguageMap = this.getCookiesInstanceLanguageMap();
        this.originCookies = safeClone(this.cookiesInstance);

        if (this.sortedInCookies.length <= 1 && this.currentPageNumber > 1) {
            this.currentPageNumber -= 1;
        }

        this.updateFilteredCookies();
    }

    cookieEntryTouched (cookie: any, indexInSortedCookies: number): void {
        const originCookie = this.originCookies.find((o: any) => o.id === cookie.id);

        const languageArrays = this.getLanguageArrays(this.sortedInCookiesWithLanguageMap[indexInSortedCookies]);
        this.sortedInCookies[indexInSortedCookies][COOKIE_FIELDS.expirations] = languageArrays[COOKIE_FIELDS.expirations];
        this.sortedInCookies[indexInSortedCookies][COOKIE_FIELDS.descriptions] = languageArrays[COOKIE_FIELDS.descriptions];

        cookie[COOKIE_FIELDS.expirations] = languageArrays[COOKIE_FIELDS.expirations];
        cookie[COOKIE_FIELDS.descriptions] = languageArrays[COOKIE_FIELDS.descriptions];

        this.trafficLights[indexInSortedCookies] = this.getTrafficLightData(cookie);

        if (!originCookie) {
            return;
        }

        const existedInToBeUpdatedIndex = this.toBeUpdated.findIndex((existed: any) => existed.id === cookie.id);
        if (existedInToBeUpdatedIndex > -1) {
            if (deepEqual(cookie, originCookie)) {
                this.toBeUpdated.splice(existedInToBeUpdatedIndex, 1);
            } else {
                this.toBeUpdated[existedInToBeUpdatedIndex] = safeClone(cookie);
            }
        } else {
            if (!deepEqual(cookie, originCookie)) {
                this.toBeUpdated.push(safeClone(cookie));
            } else {
            }
        }

        if (this.toBeUpdated.length) {
            FormUtils.markFormAsDirty(this.cookieOverviewForm);
        }
    }

    async submit (): Promise<any> {
        const toBeCreated: any[] = [];
        for (let i = 0; i < this.cookiesInstance.length; i++) {
            const cookie = this.cookiesInstance[i];
            if (!cookie.id) {
                toBeCreated.push(cookie);
            }
        }

        const firstErroredEntry = this.getInvalidCookie(toBeCreated.concat(this.toBeUpdated));
        const errorIndex = this.cookiesInstance.indexOf(firstErroredEntry);

        if (errorIndex > -1) {
            this.currentPageNumber = Math.ceil((errorIndex + 1) / this.perPage);
            return this.updateFilteredCookies().then(() => {
                this.reportFirstErroredInputValidity();
            });
        }

        const promises = [
            () => this.cookieOverviewService.removeMultipleCookies(this.toBeRemoved, this.domain.id),
            () => this.cookieOverviewService.updateMultipleCookies(this.toBeUpdated, this.domain.id),
            () => this.cookieOverviewService.createMultipleCookies(toBeCreated, this.domain.id),
        ];

        this.saving = true;

        let chain = Promise.resolve();
        promises.forEach((promise) => {
            chain = chain.then(
                () => promise(),
                (error) => {
                    return Promise.reject(error);
                },
            );
        });

        let result: any;
        try {
            for (const promise of promises) {
                result = await promise();
            }
            const createdCookies = result || [];
            this.toBeUpdated = [];
            this.toBeRemoved = [];
            this.cookiesInstance = this.getConcatedCreatedCookies(this.cookiesInstance, createdCookies);
            this.originCookies = this.getConcatedCreatedCookies(this.originCookies, createdCookies);
            this.updateFilteredCookies();
            this.saving = false;
            this.cookieOverviewService.successToast();
        } catch (error: any) {
            if (error && error.data && error.data.message) {
                this.cookieOverviewService.errorToast(error.data.message);
            }
            this.saving = false;
        }
    }

    async addLanguage (event: any): Promise<void> {
        this.showReminder = true;
        this.filledLanguages.push(event);
        this.selectedLanguage = this.filledLanguages[this.filledLanguages.length - 1].value;

        await this.replaceEmptyCookiesFieldsWithRestoredData();

        this.cookiesInstanceLanguageMap = this.getCookiesInstanceLanguageMap();
        this.updateAllCookieEntriesLanguage();
    }

    removeLanguage (event: any): void {
        this.monModalService.customDialog({
            title: this.translateService.getString('Delete language - ' + event.name),
            message: this.translateService.getString(
                'Doing this will result in removing the translations in the selected language from both your Cookie Overview and the actual banner itself.<br></br><strong>You will need to add the descriptions again when you re-add the selected language.</strong>',
            ),
            buttons: [
                {
                    label: this.translateService.getString('Cancel'),
                    className: 'btn btn-secondary',
                    value: 'cancel',
                    callback: () => {},
                },
                {
                    label: this.translateService.getString('Delete'),
                    className: 'btn btn-danger',
                    callback: () => {
                        this.showReminder = true;
                        this.filledLanguages = this.filledLanguages.filter((lang: any) => lang.value !== event.value);
                        this.selectedLanguage = this.filledLanguages[0].value;
                        this.cookiesInstanceLanguageMap = this.getCookiesInstanceLanguageMap();
                        this.updateAllCookieEntriesLanguage();
                    },
                    value: 'delete',
                },
            ],
        }, {});
    }

    onLanguageChange (): void {
        this.updateFilteredCookies();
    }

    onDemandScan (): void {
        const domainId = this.domain.id || this.sessionService.getDomainId();
        const params = { full_cookie_scan: true };
        this.domainService.rescan(domainId, params).then(() => {
            if (this.sessionService.domain !== null && domainId === this.sessionService.domain.id) {
                this.sessionService.domain.crawl_status = {} as any;
                // eslint-disable-next-line
                // @ts-ignore
                this.sessionService.domain.crawl_status.progress = 0;
                this.domain = this.sessionService.domain;
            }
        }, () => {});
    }

    onDemandScanModal (): void {
        this.monModalService.customDialog({
            title: this.translateService.getString('Start Cookie Scan'),
            message: this.translateService.getString('The scan can take a few hours/days to finalise, depending on the size of the website.<br><br>Are you sure you want to start on-demand scan?'),
            buttons: [
                {
                    label: this.translateService.getString('Cancel'),
                    className: 'btn btn-secondary',
                    callback: () => {},
                    value: 'cancel',
                },
                {
                    label: this.translateService.getString('Start Scan'),
                    className: 'btn btn-primary',
                    callback: () => this.onDemandScan(),
                    value: 'start',
                },
            ],
        }, {});
    }

    openScanDetails (): void {
        this.coreDomainService.openScanDetails();
    }

    getDomainCrawlStatus (): any {
        return this.coreDomainService.getDomainCrawlStatus();
    }

    demoAccountScanWarning (): void {
        const params = {
            title: this.translateService.getString('On-demand scan unavailable'),
            message: this.translateService.getString('This feature is not available on a demo account.'),
            buttons: [
                {
                    label: this.translateService.getString('OK'),
                    className: 'btn btn-secondary',
                    callback: () => {},
                    value: 'cancel',
                },
            ],
        };
        this.monModalService.customDialog(params, {});
    }

    resolveAbsenceWarning (cookie: any, cookieIndex: number): void {
        this.monModalService.customDialog({
            title: this.translateService.getString('Cookie not found'),
            message: this.translateService.getString(
                'It looks like the previously set up cookie was not found in this scan. It might be that the cookie was removed or our scan just couldn’t detect it.',
            ),
            buttons: [
                {
                    label: this.translateService.getString('Keep'),
                    className: 'btn btn-secondary',
                    callback: () => {
                        cookie.source = COOKIE_SOURCE.manual;
                        this.cookieEntryTouched(cookie, cookieIndex);
                    },
                    value: 'leave',
                },
                {
                    label: this.translateService.getString('Delete'),
                    className: 'btn btn-danger',
                    callback: () => {
                        this.removeCookieEntry(cookie.id, cookieIndex);
                    },
                    value: 'save',
                },
            ],
        }, {});
    }

    private getInvalidId (items: any) {
        let i = 0;
        const len = items.length;
        let item;

        for (i; i < len; i += 1) {
            item = items[i];

            if (
                ['name'].some(function (k) {
                    return !item[k] || !item[k].trim();
                })
            ) {
                return item;
            }
        }
        return undefined;
    }

    private async replaceEmptyCookiesFieldsWithRestoredData (): Promise<void> {
        if (!this.restoredCookies?.length) {
            this.restoredCookies = await this.cookieOverviewService.getCookiesFromCentralDb();
        }

        this.cookiesInstance.forEach((cookie: any) => {
            const sourceCookie = this.restoredCookies.find((it: any) =>
                it.name.trim().toLowerCase() === cookie.name?.trim().toLowerCase(),
            );
            if (!sourceCookie) {
                return;
            }

            if (!cookie.descriptions.find(it => it.language === this.selectedLanguage)) {
                const sourceDescriptions = sourceCookie.descriptions.filter(it => it.language === this.selectedLanguage);
                cookie.descriptions = [...cookie.descriptions, ...sourceDescriptions];
            }

            if (!cookie.expirations.find(it => it.language === this.selectedLanguage)) {
                const sourceExpirations = sourceCookie.expirations.filter(it => it.language === this.selectedLanguage);
                cookie.expirations = [...cookie.expirations, ...sourceExpirations];
            }

            this.updateFilteredCookies();
        });
    }

    private updateFilteredCookies (): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(() => {
                if (!Array.isArray(this.cookiesInstance)) {
                    this.cookiesInstance = [];
                }

                let updatedCookies = this.cookiesInstance.slice();
                const hasSearch = Boolean(this.search.length);
                const hasCategories = Boolean(this.filters && this.filters.category && this.filters.category.length);

                if (hasSearch) {
                    updatedCookies = this.applySearch(updatedCookies, this.search);
                }

                if (hasCategories) {
                    updatedCookies = this.applySorting(updatedCookies, this.filters.category);
                }

                this.sortedInCookies = this.setPaginatorProperties(updatedCookies, this.currentPageNumber - 1);
                this.sortedInCookiesWithLanguageMap = [];
                this.trafficLights = [];

                for (let i = 0; i < this.sortedInCookies.length; i++) {
                    this.sortedInCookiesWithLanguageMap.push(this.getLanguageMap(this.sortedInCookies[i]));
                    this.trafficLights.push(this.getTrafficLightData(this.sortedInCookies[i]));
                }

                setTimeout(() => {
                    this.adjustDropdownsWidth();
                });

                resolve();
            });
        });
    }

    private filterDuplicates (inputArr: any[]): any[] {
        const result: any[] = [];
        if (inputArr && inputArr.length) {
            const inputCopy = safeClone(inputArr) || [];
            let sourceIsNotEmpty = inputCopy.length;

            while (sourceIsNotEmpty) {
                const cookie = inputCopy.shift();
                const iIndex = this.getIndexOfDuplicateCookie(inputCopy, cookie);

                if (iIndex !== -1) {
                    const duplicate = inputCopy[iIndex];
                    if (cookie.source === COOKIE_SOURCE.manual || duplicate.source !== COOKIE_SOURCE.manual) {
                        inputCopy.splice(iIndex, 1);
                    } else {
                        continue;
                    }
                }

                const dIndex = this.getIndexOfDuplicateCookie(result, cookie);

                if (dIndex !== -1) {
                    const existing = result[dIndex];
                    if (cookie.source === COOKIE_SOURCE.manual || existing.source !== COOKIE_SOURCE.manual) {
                        result[dIndex] = cookie;
                    }
                } else {
                    result.push(cookie);
                }

                sourceIsNotEmpty = inputCopy.length;
            }
        }
        return result;
    }

    private getIndexOfDuplicateCookie (array: any[], item: any): number {
        return array.findIndex((elem: any) => this.isEqual(elem, item));
    }

    private isEqual (value: any, other: any): boolean {
        const COOKIE_PROPS = {
            NAME: 'name',
            DOMAIN: 'domain',
            PATH: 'path',
        };
        const cookieValidationProps = [COOKIE_PROPS.NAME, COOKIE_PROPS.DOMAIN, COOKIE_PROPS.PATH];
        return deepEqual(pick(value, cookieValidationProps), pick(other, cookieValidationProps));
    }

    private applySearch (cookies: any[], searchTerm: string): any[] {
        return cookies.filter((c: any) => {
            const domain = (c.domain || '').toLowerCase();
            const name = (c.name || '').toLowerCase();
            const type = (c.type || '').toLowerCase();

            let description = '';
            let expires = '';

            const descriptionWithSelectedLanguage = c.descriptions.find((desc: any) =>
                desc.language === this.selectedLanguage,
            );

            const expiresWithSelectedLanguage = c.expirations.find((exp: any) =>
                exp.language === this.selectedLanguage,
            );

            if (descriptionWithSelectedLanguage) {
                description = (descriptionWithSelectedLanguage.text || '').toLowerCase();
            }

            if (expiresWithSelectedLanguage) {
                expires = (expiresWithSelectedLanguage.text || '').toLowerCase();
            }

            return [domain, description, name, type, expires].some((text: string) =>
                text.indexOf(searchTerm.toLowerCase()) !== -1,
            );
        });
    }

    private applySorting (cookies: any[], categories: any[]): any[] {
        return cookies.filter((cookie: any) => {
            return categories.some((cat: any) => cookie.type === cat.value);
        });
    }

    private setPaginatorProperties (cookies: any[], currentIndex: number): any {
        const total = cookies.length;
        const result: any = cookies.slice(currentIndex * this.perPage, currentIndex * this.perPage + this.perPage);
        result.perPage = this.perPage;
        result.total = total;
        result.currentPage = this.currentPageNumber;
        return result;
    }

    private getInvalidCookie (items: any[]): any {
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (['name'].some((k: string) => !item[k] || item[k].trim() === '')) {
                return item;
            }
        }
        return undefined;
    }

    private getConcatedCreatedCookies (existingCookies: any[], createdCookies: any[]): any[] {
        return existingCookies
            .filter((entry: any) => entry.id != null)
            .concat(safeClone(createdCookies));
    }

    private getFilledLanguages (cookies: any[]): any[] {
        if (!cookies) {
            return [];
        }

        const resultMap: any = {};

        cookies.forEach(function (cookie) {
            const descriptions = cookie.descriptions || [];
            const expirations = cookie.expirations || [];

            descriptions.forEach(function (description) {
                if (!resultMap[description.language]) {
                    resultMap[description.language] = true;
                }
            });

            expirations.forEach(function (expiration) {
                if (!resultMap[expiration.language]) {
                    resultMap[expiration.language] = true;
                }
            });
        });

        return Object.keys(resultMap).map((key: string) => {
            const languageLabel = LOCALE_LANGUAGE_MAP[key as keyof typeof LOCALE_LANGUAGE_MAP];
            return {
                name: languageLabel ? languageLabel : key,
                value: key,
            };
        });
    }

    private getCookiesInstanceLanguageMap (): any[] {
        return this.cookiesInstance.map((cookie: any) => this.getLanguageMap(cookie));
    }

    private getLanguageMap (cookie: any): any {
        const expirations = cookie[COOKIE_FIELDS.expirations] || [];
        const descriptions = cookie[COOKIE_FIELDS.descriptions] || [];
        const expirationMap: any = {};
        const descriptionMap: any = {};
        const availableLanguages = this.filledLanguages.map((langOption: any) => langOption.value);

        expirations.forEach(function (expire) {
            if (availableLanguages.indexOf(expire.language) > -1) {
                expirationMap[expire.language] = expire.text;
            }
        });

        descriptions.forEach(function (desc) {
            if (availableLanguages.indexOf(desc.language) > -1) {
                descriptionMap[desc.language] = desc.text;
            }
        });

        this.filledLanguages.forEach(function (languageOption) {
            if (expirationMap[languageOption.value] === undefined) {
                expirationMap[languageOption.value] = '';
            }
            if (descriptionMap[languageOption.value] === undefined) {
                descriptionMap[languageOption.value] = '';
            }
        });

        return {
            expirationMap: expirationMap,
            descriptionMap: descriptionMap,
        };
    }

    private getLanguageArrays (languageMap: any): any {
        const expirationMap = languageMap.expirationMap;
        const descriptionMap = languageMap.descriptionMap;
        const result: any = {};
        result[COOKIE_FIELDS.expirations] = [];
        result[COOKIE_FIELDS.descriptions] = [];
        const availableLanguages = this.filledLanguages.map((langOption: any) => langOption.value);

        if (expirationMap) {
            result[COOKIE_FIELDS.expirations] = Object.keys(expirationMap)
                .filter(function (language) {
                    return availableLanguages.indexOf(language) > -1;
                })
                .map(function (language) {
                    return {
                        language: language,
                        text: expirationMap[language],
                    };
                });
        }

        if (descriptionMap) {
            result[COOKIE_FIELDS.descriptions] = Object.keys(descriptionMap)
                .filter(function (language) {
                    return availableLanguages.indexOf(language) > -1;
                })
                .map(function (language) {
                    return {
                        language: language,
                        text: descriptionMap[language],
                    };
                });
        }

        return result;
    }

    private updateAllCookieEntriesLanguage (): void {
        this.cookiesInstance.forEach((cookie, index) => {
            const originCookie = this.originCookies.find(function (o) {
                return o.id === cookie.id;
            });

            if (!originCookie) {
                return;
            }

            const languageArrays = this.getLanguageArrays(this.cookiesInstanceLanguageMap[index]);
            this.cookiesInstance[index][COOKIE_FIELDS.expirations] = languageArrays[COOKIE_FIELDS.expirations];
            this.cookiesInstance[index][COOKIE_FIELDS.descriptions] = languageArrays[COOKIE_FIELDS.descriptions];

            const existedInToBeUpdatedIndex = this.toBeUpdated.findIndex(function (existed) {
                return existed.id === cookie.id;
            });

            if (existedInToBeUpdatedIndex > -1) {
                // Remove the cookie from toBeUpdated array if its value is changed back to its original
                if (deepEqual(cookie, originCookie)) {
                    this.toBeUpdated.splice(existedInToBeUpdatedIndex, 1);
                }
            } else {
                // Add the cookie to toBeUpdated array if its value has been changed
                if (!deepEqual(cookie, originCookie)) {
                    this.toBeUpdated.push(cookie);
                }
            }

            if (this.toBeUpdated.length) {
                FormUtils.markFormAsDirty(this.cookieOverviewForm);
            }
        });
    }

    private getSelectedLanguageFieldText (cookie: any, fieldName: string): string {
        if (!fieldName || !cookie[fieldName] || !cookie[fieldName].length) {
            return '';
        }

        const match = cookie[fieldName].find((entry: any) => entry.language === this.selectedLanguage);

        if (match) {
            return match.text || '';
        }

        return '';
    }

    private getTrafficLightData (cookie: any): any {
        const description = this.getSelectedLanguageFieldText(cookie, 'descriptions');
        const expire = this.getSelectedLanguageFieldText(cookie, 'expirations');
        const emptyFields: string[] = [];

        [COOKIE_FIELDS.type, COOKIE_FIELDS.name, COOKIE_FIELDS.domain].forEach((key: string) => {
            const value = cookie[key];
            if (typeof value !== 'string' || value.trim() === '') {
                emptyFields.push(key);
            }
        });

        if (cookie[COOKIE_FIELDS.type] === COOKIE_CATEGORIES.unclassified && emptyFields.indexOf(COOKIE_FIELDS.type) < 0) {
            emptyFields.push(COOKIE_FIELDS.type);
        }

        if (description.trim() === '') {
            emptyFields.push(COOKIE_FIELDS.description);
        }

        if (expire.trim() === '') {
            emptyFields.push(COOKIE_FIELDS.expires);
        }

        const emptyFieldName = emptyFields.map((key) => {
            return this.cookieFieldsLocalizeService.getField(key);
        });
        let color: any = TRAFFIC_LIGHT_COLORS.green;
        let tooltip: any = this.translateService.getString('Complete');

        if (cookie.source === COOKIE_SOURCE.scanned_now_absent) {
            color = TRAFFIC_LIGHT_COLORS.yellow;
            tooltip = this.translateService.getString('Action required. Click to resolve issue.');
        } else if (emptyFieldName.length) {
            color = TRAFFIC_LIGHT_COLORS.red;
            tooltip = this.translateService.getString('Cookie not published. Missing:') + ' ' + emptyFieldName.join(', ') + '.';
        }

        return {
            color: color,
            tooltip: tooltip,
        };
    }

    private debouncedAdjustDropdownsWidth = this.debounce(this.adjustDropdownsWidth.bind(this), 300);

    private debounce (func: any, wait: number): (...args: any[]) => void {
        let timeout: any;
        return (...args: any[]) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    private adjustDropdownsWidth (): void {
        const scrollableSection = this.elementRef.nativeElement.querySelector('.middle_section');
        if (scrollableSection) {
            let cellWidth: any = undefined;
            const formSelects = scrollableSection.querySelectorAll('form-field-select');
            formSelects.forEach((el: any) => {
                const parentElement = el.parentElement;
                if (parentElement) {
                    if (!cellWidth) {
                        cellWidth = window.getComputedStyle(parentElement).width;
                    }
                    el.style.setProperty('width', cellWidth);
                    el.style.setProperty('max-width', cellWidth);
                }
            });
            scrollableSection.scrollTo(null, 0);
        }
    }

    private hackForFixingDropdownBehavior (scrollableSection: Element): void {
        const selects = scrollableSection.querySelectorAll('form-field-select');
        selects.forEach((select: any) => {
            const label = select.querySelector('label.selector-input');
            let input;
            let dropdown;

            if (label) {
                input = label.querySelector('input');
                if (input) {
                    input.addEventListener('mousedown', function (e) {
                        e.stopImmediatePropagation();
                        e.preventDefault();
                    });

                    input.addEventListener('mouseup', function (e) {
                        e.stopImmediatePropagation();
                        e.preventDefault();
                        dropdown = select.querySelector('.selector-container.open');

                        if (!dropdown && label) {
                            input.blur();
                            label.click();
                        }
                    });
                }
            }
        });
    }

    private reportFirstErroredInputValidity (): void {
        const errorInput = this.elementRef.nativeElement.querySelector('input[aria-invalid="true"]');

        if (errorInput && typeof errorInput.reportValidity === 'function') {
            errorInput.reportValidity();
        }
    }
}
